import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { showToast } from '@attentive-platform/stem-ui';

import { fetchScopeRecords, mapRecordListRow } from './api';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
/** Catalog / non-paginated consumers: hard cap so we never request unbounded payloads. */
const FULL_LIST_CAP = 500;

const STATE_MAP = {
  'All states': undefined,
  New: 'new',
  Sent: 'sent',
  Submitted: 'submitted',
  Reopened: 'reopened'
};
const OUTCOME_MAP = {
  'Pilot: open': 'open',
  'Pilot: closed won': 'closed_won',
  'Pilot: closed lost': 'closed_lost',
  'Awaiting adoption': 'awaiting_adoption',
  Live: 'live'
};
const TYPE_MAP = {
  Pilot: 'pilot',
  'Existing customer': 'existing_customer'
};
const WINDOW_MAP = {
  'Last 30 days': '30',
  'Last 90 days': '90',
  'This quarter': '90',
  'All time': 'all'
};

/** Map UI sort keys to backend `ordering` (Django-style; prefix - for desc). */
const ORDERING_FIELD = {
  email: 'customer_email',
  company: 'company_name',
  recordTypeLabel: 'record_type',
  state: 'scope_form_status',
  linkStatus: 'link_status',
  emailsEnabled: 'send_email',
  trades: 'trades_count',
  creator: 'created_by__name',
  accountOwnerSort: 'account_executive__name',
  accountOwner: 'account_executive__name',
  technicalOwnerSort: 'fde__name',
  createdAt: 'created_at',
  submittedAt: 'submitted_at',
  updatedAt: 'updated_at',
  outcomeLabel: 'outcome',
  versionNum: 'current_version__version',
  sinceSent: 'sent_at'
};

const STATE_RANK = { New: 0, Sent: 1, Reopened: 2, Submitted: 3 };
const LINK_RANK = { 'Not sent': 0, Sent: 1, Expired: 2 };

const buildParams = ({ linked, query, stateFilter, userFilter, outcomeFilter, typeFilter, dateFilter }) => ({
  linked,
  q: query?.trim() || undefined,
  scope_form_status: STATE_MAP[stateFilter],
  internal_user: userFilter && userFilter !== 'All internal users' ? userFilter : undefined,
  outcome: OUTCOME_MAP[outcomeFilter],
  record_type: TYPE_MAP[typeFilter],
  window: WINDOW_MAP[dateFilter]
});

export const compareValues = (a, b, key) => {
  if (key === 'state') return (STATE_RANK[a.state] ?? 99) - (STATE_RANK[b.state] ?? 99);
  if (key === 'linkStatus') return (LINK_RANK[a.linkStatus] ?? 99) - (LINK_RANK[b.linkStatus] ?? 99);
  if (key === 'sinceSent' || key === 'agingAnchor') {
    const at = new Date(a.agingAnchor || a.createdAt || 0).getTime();
    const bt = new Date(b.agingAnchor || b.createdAt || 0).getTime();
    if (!Number.isNaN(at) && !Number.isNaN(bt)) return at - bt;
  }

  const av = a[key];
  const bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  if (typeof av === 'boolean' && typeof bv === 'boolean') return Number(av) - Number(bv);
  if (key.endsWith('At') || key === 'updatedAt' || key === 'createdAt' || key === 'submittedAt') {
    const at = new Date(av).getTime();
    const bt = new Date(bv).getTime();
    if (!Number.isNaN(at) && !Number.isNaN(bt)) return at - bt;
  }
  return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
};

const orderingParam = sort => {
  if (!sort?.key) return undefined;
  const field = ORDERING_FIELD[sort.key];
  if (!field) return undefined;
  return sort.dir === 'desc' ? `-${field}` : field;
};

const useScopeRecords = ({
  linked,
  query,
  stateFilter,
  userFilter,
  outcomeFilter,
  typeFilter,
  dateFilter,
  sort,
  paginate = true
}) => {
  const sortKey = sort?.key;
  const sortDir = sort?.dir;
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [
    linked,
    query,
    stateFilter,
    userFilter,
    outcomeFilter,
    typeFilter,
    dateFilter,
    pageSize,
    sortKey,
    sortDir
  ]);

  useEffect(() => {
    const id = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);

      const sortState = sortKey ? { key: sortKey, dir: sortDir } : undefined;
      const base = {
        ...buildParams({
          linked,
          query,
          stateFilter,
          userFilter,
          outcomeFilter,
          typeFilter,
          dateFilter
        }),
        ordering: orderingParam(sortState)
      };

      const { data, error: err } = await fetchScopeRecords({
        ...base,
        page: paginate ? page : 1,
        limit: paginate ? pageSize : FULL_LIST_CAP,
        signal: controller.signal
      });
      if (controller.signal.aborted) return;

      if (err) {
        const msg = typeof err === 'string' ? err : err.detail || 'Failed to load records';
        showToast(msg, 'error');
        setRows([]);
        setCount(0);
        setLoading(false);
        return;
      }

      let results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
      const total = data?.count ?? results.length;
      let mapped = results.map(mapRecordListRow);

      // Soft client type filter only for non-paginated consumers (API may ignore record_type)
      const recordType = TYPE_MAP[typeFilter];
      if (!paginate && recordType) {
        mapped = mapped.filter(r => r.recordType === recordType);
      }

      // Page-local sort fallback when ordering isn't mapped / ignored by the API
      if (sortKey && !ORDERING_FIELD[sortKey]) {
        mapped = [...mapped].sort((a, b) => {
          const cmp = compareValues(a, b, sortKey);
          return sortDir === 'asc' ? cmp : -cmp;
        });
      }

      setRows(
        mapped.map(r => ({
          ...r,
          accountOwnerSort: r.accountOwner,
          technicalOwnerSort: r.technicalOwner,
          versionNum: r.version?.version ?? -1
        }))
      );
      setCount(!paginate && recordType ? mapped.length : total);
      setLoading(false);
    }, 250);
    return () => clearTimeout(id);
  }, [
    linked,
    query,
    stateFilter,
    userFilter,
    outcomeFilter,
    typeFilter,
    dateFilter,
    page,
    pageSize,
    sortKey,
    sortDir,
    paginate,
    refetchToken
  ]);

  const displayRows = useMemo(
    () =>
      rows.map(r => ({
        ...r,
        accountOwnerSort: r.accountOwner,
        technicalOwnerSort: r.technicalOwner,
        versionNum: r.version?.version ?? -1
      })),
    [rows]
  );

  const refetch = useCallback(() => setRefetchToken(t => t + 1), []);
  const pageCount = count ? Math.max(1, Math.ceil(count / pageSize)) : 0;

  return { rows: displayRows, count, loading, refetch, page, setPage, pageSize, setPageSize, pageCount };
};

export default useScopeRecords;
